import { useAppDispatch, useAppSelector } from 'appState/store'
import { upgradeBatch, upgradeBatchwithAutoCheck } from 'features/autocheck/autoCheckApi'
import { updateCurrentBatch } from 'features/autocheck/autoCheckSlice'
import { BatchType } from 'features/autocheck/types'
import { BatchValidationErrors } from 'features/autocheck/utils'
import { useUserProfile } from 'features/settings/settingsHooks'
import { IUserRoleGuidEnum } from 'features/settings/settingsTypes'
import { useState } from 'react'
import { BiLeftArrowAlt } from 'react-icons/bi'
import { BsFillCheckCircleFill } from 'react-icons/bs'
import { FaBolt, FaFileImport } from 'react-icons/fa'
import { IoMdArrowDropdown, IoMdArrowDropup } from 'react-icons/io'
import { Button } from '@outbound-ai/ursa'
import styled from 'styled-components'
import { CLAIM_S, ISSUE_S } from 'utils/constants'
import { notificator } from 'utils/notificator/notificator'

import { BatchDownloadFiles } from '../internal/BatchDownloadFiles'
import { LabelButton } from '../internal/LabelButton'

interface IProps {
  activeStep: (val: number) => void
  fileName: string
  batchType: BatchType
  batchId: string
  batchErrors: BatchValidationErrors
}

export const ClaimFileSubmit = ({ activeStep, fileName, batchType, batchId, batchErrors }: IProps) => {
  const {
    invalidFormatting,
    unSupportedPhoneNumbers,
    validforAutoCheck,
    validforImport,
    invalidOrImpreciseValues,
    totalValidClaimsForImport,
    valueOutOfBounds,
    totalInvalidForImport,
  } = batchErrors
  const { root } = useAppSelector((state) => state.batch)
  const dispatch = useAppDispatch()

  const { user } = useAppSelector((state) => state.auth)
  const { data: profile } = useUserProfile(user?.id)
  const roleIds = profile?.roles.map((role) => role.roleId)
  const isrunAutoCheckPermission = roleIds?.includes(IUserRoleGuidEnum.runAutoCheckPermission)
  const hasAutoCheckAccess = isrunAutoCheckPermission ?? true

  const [show, setShow] = useState(false)
  const [isLoading, setLoading] = useState(false)

  const handleBatchUpgrade = () => {
    setLoading(true)

    if (isrunAutoCheckPermission) {
      upgradeBatchwithAutoCheck(batchId, batchType)
        .then((res) => {
          setLoading(false)
          if (res.status === 200) {
            const preparedMessage = prepareSuccessMessage(batchType, hasAutoCheckAccess)
            notificator.notify({
              type: 'success',
              heading: `${fileName} ${preparedMessage}`,
            })
            dispatch(
              updateCurrentBatch({
                root: root,
                refresh: true,
              }),
            )
            activeStep(5)
          }
        })
        .catch((err) => {
          setLoading(false)
          console.log('🚀 ~ file: ClaimFileSubmit.tsx:31 ~ handleBatchUpgrade ~ err:', err)
          notificator.notify({
            type: 'error',
            heading: err.response?.data?.detail || err.message,
          })
          activeStep(5)
        })
    } else {
      upgradeBatch(batchId, batchType)
        .then((res) => {
          setLoading(false)
          if (res.status === 200) {
            const preparedMessage = prepareSuccessMessage(batchType, hasAutoCheckAccess)
            notificator.notify({
              type: 'success',
              heading: `${fileName} ${preparedMessage}`,
            })
            dispatch(
              updateCurrentBatch({
                root: root,
                refresh: true,
              }),
            )
            activeStep(5)
          }
        })
        .catch((err) => {
          setLoading(false)
          notificator.notify({
            type: 'error',
            heading: err.response?.data?.detail || err.message,
          })
          activeStep(5)
        })
    }
  }

  return (
    <Div>
      <div className="title-container">
        <BsFillCheckCircleFill className="check-icon" /> <p>{totalValidClaimsForImport} unique claim(s) validated</p>
      </div>
      {batchType === BatchType.Claim_Import ? (
        <span className="import-only-text">ready to upload and import</span>
      ) : (
        <div className="batch-valid-counts">
          <div>
            <FaBolt />
            {validforAutoCheck} {CLAIM_S} ready for AutoCheck
          </div>
          <div>
            <FaFileImport />
            {validforImport} {CLAIM_S} ready for Import only
          </div>
        </div>
      )}

      <div className="content-container">
        <div className="custom-toggle" onClick={() => setShow(!show)}>
          {calculateMinorIssues(batchErrors)} minor {ISSUE_S} found {show ? <IoMdArrowDropup /> : <IoMdArrowDropdown />}{' '}
        </div>
        {show && (
          <div className="toggle-container">
            <div className="content-inner">
              <LabelButton
                type="warning"
                content={
                  <div className="label-inner">
                    <b>{unSupportedPhoneNumbers.value ?? 0} </b> {unSupportedPhoneNumbers.label}
                  </div>
                }
              />
              <LabelButton
                type="warning"
                content={
                  <div className="label-inner">
                    <b>{invalidFormatting.value ?? 0} </b> {invalidFormatting.label}
                  </div>
                }
              />
              <LabelButton
                type="primary"
                content={
                  <div className="label-inner">
                    <b>{invalidOrImpreciseValues.value ?? 0} </b> {invalidOrImpreciseValues.label}
                  </div>
                }
              />
              <LabelButton
                type="secondary"
                content={
                  <div className="label-inner">
                    <b>{valueOutOfBounds.value ?? 0} </b> {valueOutOfBounds.label}
                  </div>
                }
              />
            </div>
            <p>Details are available for review in the validation reports below.</p>
            <div className="download-dropdown">{BatchDownloadFiles(batchId)}</div>
          </div>
        )}
      </div>

      <div className="footer-btn-container">
        <Button
          className="prev-btn"
          variant="secondary"
          size="small"
          radius="normal"
          onClick={() => activeStep(2)}
          disabled={totalInvalidForImport === 0}
        >
          <BiLeftArrowAlt className="download-icon" /> Previous
        </Button>
        <Button
          className="submit-btn"
          variant="success"
          size="small"
          radius="normal"
          disabled={totalValidClaimsForImport === 0}
          onClick={() => handleBatchUpgrade()}
        >
          {isLoading ? 'Processing...' : 'Upload'}
        </Button>
      </div>
    </Div>
  )
}

const Div = styled.div`
  .batch-valid-counts {
    display: flex;
    justify-content: center;
    margin-top: ${({ theme }) => theme.spacing.xs};
    align-items: center;
    gap: ${({ theme }) => theme.spacing.m};
    ${({ theme }) => theme.typography.m.normal};
    color: ${({ theme }) => theme.colors.neutral.neutral};
    svg {
      color: ${({ theme }) => theme.colors.primary.light};
      margin-right: 0.5rem;
    }
  }
  .import-only-text {
    display: flex;
    justify-content: center;
    font-weight: normal;
  }
  .title-container {
    display: flex;
    align-items: center;
    justify-content: center;

    p {
      margin-left: 0.5rem;
      font-size: 1.5rem;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: ${({ theme }) => theme.colors.neutral.neutral};
    }
    .check-icon {
      font-size: 1.5rem;
      color: ${({ theme }) => theme.colors.success.neutral};
    }
  }
  p {
    font-size: 1rem;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: center;
    color: ${({ theme }) => theme.colors.grey.dark};
  }
  .content-container {
    padding: 1.25rem 0.75rem;
    margin: ${({ theme }) => theme.spacing.l} ${({ theme }) => theme.spacing.l} ${({ theme }) => theme.spacing.xxxl}
      ${({ theme }) => theme.spacing.xxl};
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.colors.grey.lighter};
    .custom-toggle {
      cursor: pointer;
      font-size: 1.125rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.neutral.neutral};
    }
    .content-inner {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      div {
        width: 100%;
      }
      .label-inner {
        text-align: left;
        display: flex;
        align-items: center;
        b {
          margin-right: 0.5rem;
          font-size: 1rem;
          font-weight: bold;
        }
      }
    }
    p {
      margin: 1.5rem 0 0.75rem 0;
      font-size: 0.875rem;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: center;
      color: ${({ theme }) => theme.colors.grey.dark};
    }
    .toggle-container {
      transition: all 2s ease;
      margin-top: 1rem;
      .download-dropdown {
        display: flex;
        justify-content: center;
      }
    }
  }
  .footer-btn-container {
    margin: 3.75rem 2rem 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    .prev-btn,
    .submit-btn {
      width: 100%;
      height: 3rem;
      font-size: 1.125rem;
      font-weight: 600;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.33;
      letter-spacing: normal;
      text-align: center;
    }
    .prev-btn {
      margin-right: 1.5rem;
      /* color: ${({ theme }) => theme.colors.primary.neutral}; */
    }
    .submit-btn {
      color: ${({ theme }) => theme.colors.neutral.lightest};
    }
  }
  .request-eob {
    display: flex;
    align-items: center;
    justify-content: center;
    .eob-checkbox-label {
      margin-right: 0.5rem;
    }
    .eob-checkbox {
      width: 20px;
      height: 20px;
      padding: 2px;
      border-radius: 4px;
      border: solid 1px ${({ theme }) => theme.colors.info.neutral};
      background-color: ${({ theme }) => theme.colors.neutral.lightest};
      accent-color: ${({ theme }) => theme.colors.info.neutral};
    }
  }
`

function calculateMinorIssues(batchErrors: BatchValidationErrors) {
  const { invalidFormatting, invalidOrImpreciseValues, unSupportedPhoneNumbers } = batchErrors
  let total = 0
  if (invalidFormatting.value) {
    total = invalidFormatting.value
  }
  if (invalidOrImpreciseValues.value) {
    total += invalidOrImpreciseValues.value
  }
  if (unSupportedPhoneNumbers.value) {
    total += unSupportedPhoneNumbers.value
  }
  return total
}

function prepareSuccessMessage(type: BatchType, hasAutoCheckAccess: boolean) {
  if (type === BatchType.Claim_Autocheck || type === BatchType.Claim_Autocheck_EOB) {
    return `was successfully uploaded ${hasAutoCheckAccess ? '' : 'and is pending review'}`
  }
  if (type === BatchType.Claim_Import) {
    return ' was successfully imported to the claim list.'
  }
}
